import type { AccountStatus, Step } from "./lifecycle.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Phones from accounts, for the Accounts tab's levels (web/accounts/
   levels.tsx), and what counts as due today. Kept free of React so it can
   be tested on its own.
   ────────────────────────────────────────────────────────────────────────── */

export type PhoneAccount = {
  takenOn: string | null;
  phone: string | null;
  poster: string | null;
  posterName: string | null;
  accountType: "aged" | "soft_reg" | "unmarked";
  status: AccountStatus;
  step: Step;
  ticks: { warmup: (string | null)[]; posting: (string | null)[] };
  /** Lost for good, in the Graveyard: no longer on the phone (shared/appeal.ts offPhone). */
  offPhone?: boolean;
};

export const isEnded = (a: { status: AccountStatus }) => a.status === "banned" || a.status === "suspended";

/** Ticked today already: the day's work is done, so it is not waiting. */
export const tickedToday = (a: PhoneAccount, today: string) => a.ticks.warmup.at(-1) === today || a.ticks.posting.at(-1) === today;

/** Waiting on someone today: there is a next step, and it was not already ticked today. */
export const dueToday = (a: PhoneAccount, today: string) =>
  !isEnded(a) && a.step.due && !((a.step.stage === "warmup" || a.step.stage === "posting") && tickedToday(a, today));

export type PhoneGroup<A extends PhoneAccount> = { key: string; poster: string; posterName: string; label: string; type: "aged" | "soft_reg" | "unmarked"; note: string | null; plan: string[]; accounts: A[]; live: A[]; due: number };

/** A phone as the phone table has it, whether or not anything is on it. */
export type PhoneRow = { poster: string; posterName: string; label: string; accountType: "aged" | "soft_reg" | "unmarked"; note?: string | null; plan?: string[] };

/**
 * The planned slots no live account fills yet (Ben, 2026-10-28), in plan order.
 * Each live account for a creator fills one slot planned for that creator, so
 * planning AYLA twice and adding one AYLA account leaves one AYLA open.
 * Names match ignoring case, as creator names do everywhere.
 */
export function openSlots(plan: string[], live: { creator?: string | null }[]): string[] {
  const filled = new Map<string, number>();
  for (const a of live) {
    const name = a.creator?.trim().toUpperCase();
    if (name) filled.set(name, (filled.get(name) ?? 0) + 1);
  }
  const open: string[] = [];
  for (const slot of plan) {
    const name = slot.trim().toUpperCase();
    const left = filled.get(name) ?? 0;
    if (left > 0) filled.set(name, left - 1);
    else open.push(name);
  }
  return open;
}

/**
 * One group per poster's phone, by phone number (2 before 10). Phone numbers repeat across posters.
 * Phones with no accounts come from `phones`: built from accounts alone, an empty phone had no
 * box, and on 2026-10-19 Jess reported her empty phones 10, 15 and 23 as missing.
 */
export function groupPhones<A extends PhoneAccount>(accounts: A[], today: string, phones: PhoneRow[] = []): PhoneGroup<A>[] {
  const map = new Map<string, PhoneGroup<A>>();
  for (const p of phones) {
    const key = `${p.poster}|${p.label}`;
    map.set(key, { key, poster: p.poster, posterName: p.posterName, label: p.label, type: p.accountType, note: p.note ?? null, plan: p.plan ?? [], accounts: [], live: [], due: 0 });
  }
  for (const a of accounts) {
    // On a phone means it has a phone (imported accounts have no take date).
    if (!a.phone || !a.poster || a.offPhone) continue;
    const key = `${a.poster}|${a.phone}`;
    let g = map.get(key);
    if (!g) {
      g = { key, poster: a.poster, posterName: a.posterName ?? "Someone", label: a.phone, type: a.accountType, note: null, plan: [], accounts: [], live: [], due: 0 };
      map.set(key, g);
    }
    g.accounts.push(a);
    if (!isEnded(a)) {
      g.live.push(a);
      if (dueToday(a, today)) g.due++;
    }
  }
  return [...map.values()].sort((x, y) => x.label.localeCompare(y.label, undefined, { numeric: true }));
}
